import type { IdOrIdentifier } from "~/tools/id-or-identifier";

import type { DashboardSpec } from "../../../common/dashboard-spec";
import { useErrorMessage } from "../../../components/_notification-error";
import { notification } from "../../../components/react/notification/Notification";
import { useOptimisticUpdateDashboard } from "../../../queries/dashboards";

export interface UseSaveDashboardParams {
	idOrIdentifier: IdOrIdentifier;
	revision: undefined | DashboardSpec;
}

export function useSaveDashboard({
	onError,
}: {
	onError?: (error: Error, params: UseSaveDashboardParams) => void;
} = {}) {
	const { displayErrorMessage } = useErrorMessage();

	const { mutate } = useOptimisticUpdateDashboard({
		onSuccess: ({ name }) => {
			notification.success({
				message: `The dashboard "${name}" has been successfully saved.`,
			});
		},

		onError(error, { idOrIdentifier, diff }) {
			if (onError) {
				onError(error, { idOrIdentifier, revision: diff.spec });
			} else {
				displayErrorMessage({
					message: "Something went wrong while trying to save the dashboard.",
					error,
				});
			}
		},
	});

	return ({ idOrIdentifier, revision }: UseSaveDashboardParams): void => {
		mutate({
			idOrIdentifier,
			diff: { spec: revision },
		});
	};
}
